import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays } from "lucide-react";

// Works entirely in-app (no native iOS picker). value/onChange use "yyyy-MM-dd".
const parseISO = (s) => {
  const m = String(s || "").match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]); // local midnight — avoids timezone drift
  return isNaN(d.getTime()) ? null : d;
};
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const labelFor = (d) => (d ? d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "");

const calClassNames = {
  months: "w-full",
  month: "w-full space-y-3",
  caption: "flex justify-center pt-1 relative items-center",
  caption_label: "text-sm font-600 text-zinc-100",
  table: "w-full border-collapse",
  head_row: "flex w-full",
  head_cell: "flex-1 text-center text-[0.75rem] font-normal text-zinc-400",
  row: "flex w-full mt-1.5",
  cell: "flex-1 relative p-0.5 text-center text-sm focus-within:relative focus-within:z-20",
  day: "mx-auto flex h-10 w-full items-center justify-center rounded-md p-0 font-normal text-zinc-100 hover:bg-zinc-800 aria-selected:opacity-100",
  day_selected: "bg-[#9B2226] text-white hover:bg-[#7A1A1E] hover:text-white focus:bg-[#9B2226]",
  day_today: "ring-1 ring-inset ring-[#E0575B]/70 text-[#E0575B]",
  day_outside: "text-zinc-600",
  day_disabled: "text-zinc-700 opacity-40",
};

export const SingleDatePicker = ({ value, onChange, testid, placeholder = "Pick a date", className = "" }) => {
  const [open, setOpen] = useState(false);
  const date = parseISO(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`flex h-10 items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 text-left text-sm transition-colors hover:bg-zinc-800 ${className}`}
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-[#E0575B]" />
          {date ? (
            <span className="truncate text-zinc-100" data-testid={testid ? `${testid}-value` : undefined}>{labelFor(date)}</span>
          ) : (
            <span className="truncate text-zinc-400">{placeholder}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[320px] max-w-[calc(100vw-2rem)] border-zinc-800 bg-zinc-950 p-3"
        data-testid={testid ? `${testid}-calendar` : undefined}
      >
        <Calendar
          mode="single"
          selected={date || undefined}
          defaultMonth={date || undefined}
          onSelect={(d) => { if (d) { onChange(toISO(d)); setOpen(false); } }}
          className="w-full p-0"
          classNames={calClassNames}
        />
        <button
          type="button"
          onClick={() => { onChange(toISO(new Date())); setOpen(false); }}
          className="mt-2 w-full rounded-md border border-zinc-800 bg-zinc-900 py-2 text-sm font-600 text-[#E0575B] transition-colors hover:bg-zinc-800"
          data-testid={testid ? `${testid}-today` : undefined}
        >
          Today
        </button>
      </PopoverContent>
    </Popover>
  );
};
